import React, { useState } from 'react';
import axios from 'axios';
import ClassModal from './ClassModal';
import AssignmentUploadModal from './AssignmentUploadModal';

function ClassManagement({ classes, discipline = 'stem', onClassesChange, user }) {
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [showManagement, setShowManagement] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedClassForUpload, setSelectedClassForUpload] = useState(null);
  const [showAllClasses, setShowAllClasses] = useState(false);

  const handleAddClass = () => {
    setEditingClass(null);
    setShowModal(true);
  };

  const handleEditClass = (cls) => {
    setEditingClass(cls);
    setShowModal(true);
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class? All associated evidence will remain but will need to be reassigned.')) {
      return;
    }

    try {
      await axios.delete(`/outcomes/api/classes/${classId}`, {
        withCredentials: true
      });
      
      onClassesChange();
    } catch (error) {
      console.error('Failed to delete class:', error);
      alert('Failed to delete class');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingClass(null);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setEditingClass(null);
    onClassesChange();
  };

  const handleOpenUploadModal = (cls) => {
    setSelectedClassForUpload(cls);
    setShowUploadModal(true);
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    setSelectedClassForUpload(null);
    onClassesChange();
  };

  const gradeLabels = {
    'prek2': 'PreK-2',
    'grade35': 'Grades 3-5',
    'grade68': 'Grades 6-8',
    'grade912': 'Grades 9-12'
  };

  // Filter classes based on showAllClasses toggle
  const displayClasses = (showAllClasses 
    ? classes 
    : classes.filter(cls => cls.discipline === discipline))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="class-management">
      <div className="class-management-header">
        <button 
          className="manage-classes-btn"
          onClick={() => setShowManagement(!showManagement)}
        >
          📚 Manage Classes ({displayClasses.length})
        </button>
        {user && user.is_admin && (
          <button 
            className="toggle-all-classes-btn"
            onClick={() => setShowAllClasses(!showAllClasses)}
            title={showAllClasses ? 'Showing all classes' : 'Showing discipline classes'}
            style={{
              padding: '8px 12px',
              marginRight: '8px',
              backgroundColor: showAllClasses ? '#4CAF50' : '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {showAllClasses ? '🌐 All Classes' : '📖 By Discipline'}
          </button>
        )}
        <button className="add-class-btn" onClick={handleAddClass}>
          + Add Class
        </button>
      </div>

      {showManagement && (
        <div className="class-list-panel">
          <h3>{showAllClasses ? 'All Classes' : 'Classes for ' + (discipline.charAt(0).toUpperCase() + discipline.slice(1))}</h3>
          {displayClasses.length === 0 ? (
            <div className="no-classes">
              <p>No classes yet. Click "Add Class" to get started!</p>
            </div>
          ) : (
            <div className="class-grid">
              {displayClasses.map((cls) => (
                <div key={cls.id} className="class-card">
                  <div className="class-card-header">
                    <div className="class-name">{cls.name}</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {showAllClasses && (
                        <div className="class-discipline-badge" style={{
                          padding: '4px 8px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}>
                          {cls.discipline}
                        </div>
                      )}
                      <div className="class-grade-badge">
                        {gradeLabels[cls.grade_level]}
                      </div>
                    </div>
                  </div>
                  {cls.description && (
                    <div className="class-description">{cls.description}</div>
                  )}
                  <div className="class-card-actions">
                    <button 
                      className="class-action-btn edit-btn"
                      onClick={() => handleEditClass(cls)}
                    >
                      ✏️ Edit
                    </button>
                    {user && user.is_admin && (
                      <button 
                        className="class-action-btn import-btn"
                        onClick={() => handleOpenUploadModal(cls)}
                      >
                        📤 Import
                      </button>
                    )}
                    {user && user.email === 'phitaylr@gmail.com' && (
                      <button 
                        className="class-action-btn delete-btn"
                        onClick={() => handleDeleteClass(cls.id)}
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showModal && (
        <ClassModal
          classData={editingClass}
          discipline={discipline}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}

      {showUploadModal && selectedClassForUpload && (
        <AssignmentUploadModal
          classId={selectedClassForUpload.id}
          classGradeLevel={selectedClassForUpload.grade_level}
          discipline={discipline}
          onClose={() => {
            setShowUploadModal(false);
            setSelectedClassForUpload(null);
          }}
          onUploadSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
}

export default ClassManagement;